import React, { useState, useEffect } from 'react';
import { X, Package, User, BookOpen, Wifi, Users, DollarSign } from 'lucide-react';
import api from '../utils/api';
import { HourPackageType, AdminPackageAssignRequest } from '../types/hourPackages';

interface AssignPackageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface Student {
  id: string;
  name: string;
  email: string;
}

interface Subject {
  id: string;
  name: string;
  category: 'ELEVEN_PLUS' | 'GCSE' | 'A_LEVEL' | 'UNIVERSITY';
  onlineRate: number;
  inPersonRate: number;
}

interface PackageConfig {
  id: string;
  packageType: HourPackageType;
  name?: string;
  hours: number;
  discountPercent: number;
  sortOrder?: number;
  isActive: boolean;
}

const AssignPackageModal: React.FC<AssignPackageModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [students, setStudents] = useState<Student[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [packageConfigs, setPackageConfigs] = useState<PackageConfig[]>([]);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  const [selectedPackageType, setSelectedPackageType] = useState<HourPackageType>('');
  const [isOnline, setIsOnline] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [calculatedPrice, setCalculatedPrice] = useState<{
    originalPrice: number;
    discountedPrice: number;
    discountAmount: number;
  } | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  useEffect(() => {
    calculatePrice();
  }, [selectedSubjectId, selectedPackageType, isOnline, subjects, packageConfigs]);

  const fetchData = async () => {
    try {
      const [studentsRes, subjectsRes, configsRes] = await Promise.all([
        api.get('/admin/students'),
        api.get('/subjects?includeInactive=false'),
        api.get('/hour-packages/configs')
      ]);

      setStudents(studentsRes.data);
      setSubjects(subjectsRes.data);
      const activeConfigs = (configsRes.data as PackageConfig[]).filter((c) => c.isActive);
      setPackageConfigs(activeConfigs);

      if (studentsRes.data.length > 0) {
        setSelectedStudentId(studentsRes.data[0].id);
      }
      if (subjectsRes.data.length > 0) {
        setSelectedSubjectId(subjectsRes.data[0].id);
      }
      if (activeConfigs.length > 0) {
        setSelectedPackageType(activeConfigs[0].packageType);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setError('Failed to load data');
    }
  };

  const calculatePrice = () => {
    if (!selectedSubjectId || !selectedPackageType) {
      setCalculatedPrice(null);
      return;
    }

    const subject = subjects.find(s => s.id === selectedSubjectId);
    const config = packageConfigs.find(c => c.packageType === selectedPackageType && c.isActive);

    if (!subject || !config) {
      setCalculatedPrice(null);
      return;
    }

    const rate = isOnline ? subject.onlineRate : subject.inPersonRate;
    const originalPrice = rate * config.hours;
    const discountAmount = originalPrice * (config.discountPercent / 100);
    const discountedPrice = originalPrice - discountAmount;

    setCalculatedPrice({
      originalPrice: Math.round(originalPrice * 100) / 100,
      discountedPrice: Math.round(discountedPrice * 100) / 100,
      discountAmount: Math.round(discountAmount * 100) / 100
    });
  };

  const handleAssign = async () => {
    if (!selectedStudentId || !selectedSubjectId || !selectedPackageType) {
      setError('Please select all required fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const requestData: AdminPackageAssignRequest = {
        studentId: selectedStudentId,
        subjectId: selectedSubjectId,
        packageType: selectedPackageType,
        isOnline
      };

      await api.post('/hour-packages/admin/assign', requestData);
      onSuccess();
      onClose();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to assign package');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <div className="flex items-center space-x-2">
            <Package className="w-6 h-6 text-primary-600" />
            <h2 className="text-2xl font-bold text-gray-900">Assign Hour Package</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md">
              {error}
            </div>
          )}

          <div className="space-y-4">
            {/* Student Selection */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <User className="w-4 h-4 mr-1" />
                Select Student
              </label>
              <select
                value={selectedStudentId}
                onChange={(e) => setSelectedStudentId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select a student...</option>
                {students.map(student => (
                  <option key={student.id} value={student.id}>
                    {student.name} ({student.email})
                  </option>
                ))}
              </select>
            </div>

            {/* Subject Selection */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <BookOpen className="w-4 h-4 mr-1" />
                Select Subject
              </label>
              <select
                value={selectedSubjectId}
                onChange={(e) => setSelectedSubjectId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select a subject...</option>
                {subjects.map(subject => (
                  <option key={subject.id} value={subject.id}>
                    {subject.name} ({subject.category.replace('_', ' ')})
                  </option>
                ))}
              </select>
            </div>

            {/* Package Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Package Type
              </label>
              {packageConfigs.length === 0 ? (
                <div className="text-sm text-gray-500 italic">
                  No packages configured. Add one in Admin → Financials → Package Settings.
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {packageConfigs
                    .slice()
                    .sort((a, b) => ((a.sortOrder ?? 0) - (b.sortOrder ?? 0)) || (a.hours - b.hours))
                    .map(cfg => (
                      <button
                        key={cfg.id}
                        onClick={() => setSelectedPackageType(cfg.packageType)}
                        className={`p-3 border-2 rounded-lg transition-colors text-left ${
                          selectedPackageType === cfg.packageType
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <div className="font-semibold">{cfg.name || `${cfg.hours} Hours`}</div>
                        <div className="text-sm text-gray-600">
                          {cfg.hours} hrs &middot; {cfg.discountPercent}% off
                        </div>
                      </button>
                    ))}
                </div>
              )}
            </div>

            {/* Platform Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Lesson Platform
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setIsOnline(true)}
                  className={`p-3 border-2 rounded-lg transition-colors flex items-center justify-center space-x-2 ${
                    isOnline
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <Wifi className="w-4 h-4" />
                  <span>Online</span>
                </button>
                <button
                  onClick={() => setIsOnline(false)}
                  className={`p-3 border-2 rounded-lg transition-colors flex items-center justify-center space-x-2 ${
                    !isOnline
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <Users className="w-4 h-4" />
                  <span>In-Person</span>
                </button>
              </div>
            </div>

            {/* Price Calculation Display */}
            {calculatedPrice && (
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <DollarSign className="w-5 h-5 text-primary-600 mr-2" />
                  <h3 className="font-semibold text-gray-900">Package Pricing</h3>
                </div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Original Price:</span>
                    <span className="text-gray-900">£{calculatedPrice.originalPrice.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Discount:</span>
                    <span className="text-red-600">-£{calculatedPrice.discountAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-semibold pt-1 border-t">
                    <span className="text-gray-900">Final Price:</span>
                    <span className="text-green-600">£{calculatedPrice.discountedPrice.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAssign}
              disabled={loading || !selectedStudentId || !selectedSubjectId}
              className="px-4 py-2 text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Assigning...' : 'Assign Package'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignPackageModal;